import React, { useState } from 'react';
import { UploadIcon, PlusIcon, XIcon, CheckIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useRegistry } from '../../contexts/RegistryContext';
const field = 'mt-2 w-full border border-navy-100 bg-white px-3.5 py-2.5 font-sans text-[0.875rem] text-ink placeholder:text-charcoal-200 transition-colors duration-120 hover:border-navy-300 focus:border-navy-500';
const label = 'block font-sans text-[0.8125rem] font-medium text-ink';
const blank = {
  fullName: '',
  email: '',
  professionalTitle: '',
  yearsExperience: '',
  linkedInUrl: '',
  motivation: ''
};

/** Split a CSV line, honouring quoted fields. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      out.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  out.push(current);
  return out.map(v => v.trim());
}
export function ImportRegistrations() {
  const {
    addRegistration
  } = useRegistry();
  const [mode, setMode] = useState<'closed' | 'manual' | 'csv'>('closed');
  const [entry, setEntry] = useState(blank);
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState('');
  const set = (key: keyof typeof blank) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEntry(prev => ({
    ...prev,
    [key]: e.target.value
  }));
  const importCsv = () => {
    const lines = csv.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) {
      setResult('That does not look like a CSV with a header row and at least one entry.');
      return;
    }
    const headers = splitCsvLine(lines[0]).map(h => h.toLowerCase());
    const index = (...names: string[]) => headers.findIndex(h => names.some(n => h.includes(n)));
    const iName = index('name');
    const iEmail = index('email');
    const iTitle = index('title', 'role');
    const iYears = index('year', 'experience');
    const iLinked = index('linkedin');
    const iWhy = index('motivation', 'why');
    if (iName === -1 || iEmail === -1) {
      setResult('The CSV needs at least a name column and an email column.');
      return;
    }
    let added = 0;
    lines.slice(1).forEach(line => {
      const cells = splitCsvLine(line);
      const fullName = cells[iName] ?? '';
      const email = cells[iEmail] ?? '';
      if (!fullName || !email) return;
      addRegistration({
        fullName,
        email,
        professionalTitle: iTitle > -1 ? cells[iTitle] ?? '' : '',
        yearsExperience: iYears > -1 ? cells[iYears] ?? '' : '',
        linkedInUrl: iLinked > -1 ? cells[iLinked] ?? '' : '',
        motivation: iWhy > -1 ? cells[iWhy] ?? '' : ''
      });
      added += 1;
    });
    setResult(added ? `${added} registration${added === 1 ? '' : 's'} added.` : 'Nothing could be read from that CSV.');
    if (added) setCsv('');
  };
  if (mode === 'closed') {
    return <div className="mt-6 flex flex-wrap gap-2" data-id="element-82" data-mp="1eizc60ajoes3">
        <Button size="sm" variant="secondary" onClick={() => setMode('manual')} data-id="element-83" data-mp="1efle348wyrqg">
          <PlusIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-84" data-mp="1aifij6p6jtap" />
          Add a registration
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setMode('csv')} data-id="element-85" data-mp="1odrax7fv57og">
          <UploadIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-86" data-mp="1jgr60zur2ayx" />
          Import from CSV
        </Button>
      </div>;
  }
  return <div className="mt-6 border-l-2 border-ink bg-white p-6" data-id="element-87" data-mp="1t72n7o3g2gnr">
      <div className="flex items-start justify-between gap-4" data-id="element-88" data-mp="1t7m1mutvymx1">
        <div data-id="element-89" data-mp="277n5mj5g6agq">
          <h3 className="font-serif text-[1.125rem] text-ink" data-id="element-90" data-mp="277dayi9gjf4p">
            {mode === 'manual' ? 'Add a registration' : 'Import registrations from CSV'}
          </h3>
          <p className="mt-1.5 max-w-2xl font-sans text-[0.8125rem] leading-relaxed text-charcoal-500" data-id="element-91" data-mp="iuxlkb3tu87">
            {mode === 'manual' ? 'Use this to record someone who registered on another device, or who came to you by email or phone.' : 'Paste a CSV with a header row. A name column and an email column are required; title, years, LinkedIn and motivation are used if present.'}
          </p>
        </div>
        <button type="button" onClick={() => {
        setMode('closed');
        setResult('');
      }} className="inline-flex items-center gap-1.5 font-sans text-[0.8125rem] text-charcoal-500 underline underline-offset-2 hover:text-ink" data-id="element-92" data-mp="3dqfvbj2pbz2q">
          <XIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-93" data-mp="3rpfliv5v4m10" />
          Close
        </button>
      </div>

      {mode === 'manual' ? <form className="mt-6" onSubmit={e => {
      e.preventDefault();
      if (!entry.fullName.trim() || !entry.email.trim()) return;
      addRegistration({
        fullName: entry.fullName.trim(),
        email: entry.email.trim(),
        professionalTitle: entry.professionalTitle.trim(),
        yearsExperience: entry.yearsExperience.trim(),
        linkedInUrl: entry.linkedInUrl.trim(),
        motivation: entry.motivation.trim()
      });
      setEntry(blank);
      setResult(`${entry.fullName.trim()} added.`);
    }} data-id="element-94" data-mp="121ok3n8iyis3">
          <div className="grid gap-5 sm:grid-cols-2" data-id="element-95" data-mp="tbpzsnnyi16h">
            <div data-id="element-96" data-mp="odoia766i9x4">
              <label htmlFor="imp-name" className={label} data-id="element-97" data-mp="od4z6n4slfhi">
                Full name <span className="text-gold-700" data-id="element-98" data-mp="ofhqhqwfx278">*</span>
              </label>
              <input id="imp-name" required value={entry.fullName} onChange={set('fullName')} className={field} data-id="element-99" data-mp="kdw6lllhnnnj" />
            </div>
            <div data-id="element-100" data-mp="2eq47dfkhqyr8">
              <label htmlFor="imp-email" className={label} data-id="element-101" data-mp="2epkptzyr6cdq">
                Email <span className="text-gold-700" data-id="element-102" data-mp="2eo9lnz1eb2m3">*</span>
              </label>
              <input id="imp-email" type="email" required value={entry.email} onChange={set('email')} className={field} data-id="element-103" data-mp="c1vsl97lp1zk" />
            </div>
            <div data-id="element-104" data-mp="32quu53v5cog">
              <label htmlFor="imp-title" className={label} data-id="element-105" data-mp="33adxp592742">
                Professional title
              </label>
              <input id="imp-title" value={entry.professionalTitle} onChange={set('professionalTitle')} className={field} data-id="element-106" data-mp="75dtopyybxv5" />
            </div>
            <div data-id="element-107" data-mp="uru5q3q99la2">
              <label htmlFor="imp-years" className={label} data-id="element-108" data-mp="zt1dw3wb8mh9">
                Years of experience
              </label>
              <input id="imp-years" value={entry.yearsExperience} onChange={set('yearsExperience')} className={field} data-id="element-109" data-mp="ltrvqyojnian" />
            </div>
            <div className="sm:col-span-2" data-id="element-110" data-mp="qqs27apb2n2a">
              <label htmlFor="imp-linked" className={label} data-id="element-111" data-mp="36jqzevyk357g">
                LinkedIn profile
              </label>
              <input id="imp-linked" value={entry.linkedInUrl} onChange={set('linkedInUrl')} className={field} data-id="element-112" data-mp="3bj3olncwgod7" />
            </div>
            <div className="sm:col-span-2" data-id="element-113" data-mp="3308rq1h4771p">
              <label htmlFor="imp-why" className={label} data-id="element-114" data-mp="2yzyi8eu45yi3">
                Why they want to work with us
              </label>
              <textarea id="imp-why" rows={3} value={entry.motivation} onChange={set('motivation')} className={`${field} resize-y`} data-id="element-115" data-mp="3czzj3uadp5xk" />
            </div>
          </div>
          <div className="mt-6 border-t border-navy-100 pt-5" data-id="element-116" data-mp="37ytm3k9oqwi8">
            <Button type="submit" data-id="element-117" data-mp="2kblj58zqbzjd">Add registration</Button>
          </div>
        </form> : <div className="mt-6" data-id="element-118" data-mp="2fdk4d09c6mdp">
          <label htmlFor="imp-csv" className={label} data-id="element-119" data-mp="2fa4o3u3xf7ap">
            CSV contents
          </label>
          <textarea id="imp-csv" rows={8} value={csv} onChange={e => setCsv(e.target.value)} placeholder={'Name,Email,Title,Years\nAdaeze Balogun,a.balogun@example.com,Chief Operating Officer,18'} className={`${field} resize-y font-mono text-[0.8125rem]`} data-id="element-120" data-mp="2u2onqkrvawne" />
          <div className="mt-5 flex flex-wrap gap-3 border-t border-navy-100 pt-5" data-id="element-121" data-mp="1b2y1kyjzie1s">
            <Button onClick={importCsv} disabled={!csv.trim()} data-id="element-122" data-mp="1g0px02ju8iaq">
              <UploadIcon aria-hidden="true" className="h-4 w-4" data-id="element-123" data-mp="1g32nx57kq2zx" />
              Import
            </Button>
            <label className="inline-flex cursor-pointer items-center gap-2 border border-navy-200 px-4 py-2.5 font-sans text-[0.875rem] text-ink transition-colors duration-120 hover:border-navy-500" data-id="element-124" data-mp="1pzffnxjk66s1">
              Choose a .csv file
              <input type="file" accept=".csv,text/csv" className="sr-only" onChange={e => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => setCsv(String(reader.result ?? ''));
            reader.readAsText(file);
          }} data-id="element-125" data-mp="1yzbwxe3mc3d2" />
            </label>
          </div>
        </div>}

      {result ? <p className="mt-4 inline-flex items-center gap-1.5 font-sans text-[0.8125rem] text-state-positive" data-id="element-126" data-mp="64t4fbzh0zld">
          <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-127" data-mp="20d0hg9f428s" />
          {result}
        </p> : null}
    </div>;
}